import { useEffect, useRef } from "react";

import type { DispatchAction } from "@storyboard/dispatch-view/view";
import { actionForKeyEvent, type DispatchShortcut } from "./dispatchKeymap";

/**
 * Handlers the Dispatch shortcuts invoke, the rebindable actions and the fixed selection keys; the latest closures are
 * always used. Each gets the key event that fired it, so a handler can tell apart the keys that share its action.
 */
export type DispatchShortcutActions = Record<DispatchShortcut, (event: KeyboardEvent) => void>;

/** Stamped on the side panel's root, so a text field inside it can be told from one on the page. */
export const DISPATCH_PANEL_ATTRIBUTE = "data-dispatch-panel";

/** The keys that page the open popover tab, which reach it from the panel's own composer. */
const PAGE_ACTIONS: ReadonlySet<DispatchShortcut> = new Set(["nextPage", "prevPage"]);

/** Input types that take no typing, so shortcuts stay live while one has focus (a ticked checkbox, say). */
const NON_TEXT_INPUT_TYPES = new Set(["checkbox", "radio", "button", "submit", "range"]);

/** Whether the event target is a place the user types, where single-key shortcuts must stay inert. */
export function isTextEntryTarget(target: EventTarget | null): target is HTMLElement {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(target.type);
  return target.tagName === "TEXTAREA" || target.isContentEditable;
}

/** Whether the event target is a checkbox, whose own Space toggle must not be doubled by `toggleSelect`. */
function isCheckboxTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement && target.type === "checkbox";
}

/**
 * Whether a MUI menu or popover is open (a filter control's list, a header context menu). They keep focus on list
 * items that pass the text-entry guard, so shortcuts would otherwise fire behind them.
 */
function isMenuOverlayOpen(): boolean {
  return document.querySelector(".MuiPopover-root, .MuiMenu-root") != null;
}

/**
 * Binds the resolved Dispatch keymap to its handlers on the document.
 *
 * The keys are rebindable through the page's keymap except the spreadsheet selection keys (Shift with an arrow, Ctrl
 * or Cmd with A), which `actionForKeyEvent` fixes. What each key does in single-driver mode versus with rows ticked is the page's
 * decision; see `dispatchModel.ts`.
 *
 * While a text field is focused only `closePanel` is live, and it only blurs the field: the first Escape leaves the
 * box and hands the keys back to the table, and the next one closes whatever is open. Nothing puts the cursor in a
 * field unasked (the `reply` key does, on purpose), so no field needs Escape to do both at once. The composer owns
 * `send` on its own Enter, and every other key is the field's to type, but for the page keys while the popover is
 * open: from the panel's composer they page its tab (History or Context) as they do from the table. The search box
 * takes its own ↓ and Enter into its results (`DispatchSearchBox`) and prevents them, so they never reach here.
 * Nothing fires while `enabled` is false.
 *
 * @param keymap - The resolved bindings, one per action.
 * @param actions - Handlers per action.
 * @param enabled - Whether the shortcuts are active.
 * @param panelShown - Whether the popover is open on either tab, which the page keys page even from its composer.
 */
export function useDispatchShortcuts(
  keymap: Record<DispatchAction, string>,
  actions: DispatchShortcutActions,
  enabled: boolean,
  panelShown = false,
): void {
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const panelShownRef = useRef(panelShown);
  panelShownRef.current = panelShown;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing) return;
      const action = actionForKeyEvent(event, keymap);
      if (!action) return;

      if (isTextEntryTarget(event.target)) {
        if (
          PAGE_ACTIONS.has(action) &&
          panelShownRef.current &&
          event.target.closest(`[${DISPATCH_PANEL_ATTRIBUTE}]`)
        ) {
          event.preventDefault();
          actionsRef.current[action](event);
          return;
        }
        if (action !== "closePanel") return;
        event.preventDefault();
        event.target.blur();
        return;
      }
      // Enter has nothing to send outside the composer; the composer handles its own.
      if (action === "send") return;
      // A focused checkbox ticks itself on Space; the page's tick would undo it.
      if (action === "toggleSelect" && isCheckboxTarget(event.target)) return;
      if (isMenuOverlayOpen()) return;

      event.preventDefault();
      actionsRef.current[action](event);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [keymap, enabled]);
}
